// @ts-strict-ignore
import { isMatrix1Dimensional } from '~/dashboard/views/workflows/service/actions';
import { ActionType } from '../../../constants';
import type {
  Action,
  LegacyAction,
  LegacyMatrixModelResponse,
  Outcome,
  Tag,
} from '../../../types';
import { getActiveOutcomes, getOutcomeByName } from '~/dashboard/views/workflows/service/outcomes/outcomes';
import { getShouldDefineValueRanges } from 'flow/editor/components/NodeEditorDrawer/MatrixModel/helpers';

export function reverseTransformTagTypeAction({
  tags,
  action,
}: {
  tags: Tag[];
  action: Action;
}) {
  const matchingTagObject = tags.find(tag => tag.tag_name.toLowerCase()
    === action.tag.name.toLowerCase() && tag.archived === false) || {} as Tag;

  const tagData = {
    name: action.tag.name,
    tagColor: action.tag.color,
    customerTagId: matchingTagObject?.customer_tag_id,
    applicationId: null,
    booleanRule: '',
    thresholdsRule: '',
    customerOutcomeId: null,
    options: {},
    precedence: null,
    publishedAttributeToken: null,
    serviceFailureDefault: action.serviceFailureDefault,
    serviceId: null,
  };

  return tagData;
}

export function reverseTransformRunTypeAction({
  services,
  action,
}: {
  services: Record<number, Record<
  string, string | number | object | null
  >>;
  action: Action;
}) {
  const matchingServiceObject = Object.values(services).find(
    service => service.internal_name === action.service.internalName);

  const serviceData = {
    name: action.service.name,
    tagColor: null,
    customerTagId: null,
    applicationId: null,
    booleanRule: '',
    thresholdsRule: '',
    customerOutcomeId: null,
    options: {},
    precedence: null,
    publishedAttributeToken: null,
    serviceFailureDefault: action.serviceFailureDefault,
    serviceId: matchingServiceObject?.service_id,
    credentialOwner: action.service.credentialOwner,
  };

  return serviceData;
}

export function reverseTransformOutcomeTypeAction({
  outcomes,
  action,
}: {
  outcomes: Outcome[];
  action: Action;
}) {
  const matchingOutcomeObject = getOutcomeByName(getActiveOutcomes(outcomes), action.outcome.name);
  const outcomeData = {
    name: action.outcome.name,
    tagColor: null,
    customerTagId: null,
    applicationId: null,
    booleanRule: '',
    thresholdsRule: '',
    customerOutcomeId: matchingOutcomeObject?.customer_outcome_id,
    options: {},
    precedence: action.outcome.precedenceIndex,
    publishedAttributeToken: null,
    serviceFailureDefault: null,
    serviceId: null,
  };

  return outcomeData;
}

export function reverseTransformInputOutputTypeAction({
  action,
  actionIndex,
  transformedActionOptionIndexNumber,
}: {
  action: Action;
  actionIndex: number;
  transformedActionOptionIndexNumber: number;
}) {
  const actionData = {
    name: action.input?.name || action.output?.name,
    publishedAttributeToken:
      action.input?.publishedAttributeToken || action.output?.publishedAttributeToken || null,
    tagColor: null,
    customerTagId: null,
    applicationId: null,
    booleanRule: '',
    thresholdsRule: '',
    customerOutcomeId: null,
    options: {},
    precedence: null,
    serviceFailureDefault: null,
    serviceId: null,
  };

  let actionOptionData = {};
  const actionOptionBaseData = {
    publicId: action.input?.actionOptionPublicId || action.output?.actionOptionPublicId,
    publishedAttributeToken:
      action.input?.publishedAttributeToken || action.output?.publishedAttributeToken || null,
    actionId: actionIndex,
    id: 0,
    key: '',
    value: '',
    jsonValue: null,
  };

  if (action.type === 'input' && !action.input.publishedAttributeToken) {
    actionOptionData = {
      data_type: {
        ...actionOptionBaseData,
        id: transformedActionOptionIndexNumber + 2,
        key: 'data_type',
        value: action.input.dataType,
      },
      name: {
        ...actionOptionBaseData,
        id: transformedActionOptionIndexNumber + 3,
        key: 'name',
        value: action.input.name,
      },
      use_jq: {
        ...actionOptionBaseData,
        id: transformedActionOptionIndexNumber + 4,
        key: 'use_jq',
        /**
         * todo BUILD-3170:
         * the value is guarded against `undefined` specifically because keys with `undefined` are
         * removed during serialization (JSON.stringify) as the data is sent to the server.
         * `undefined` happens when the action does not have this action option on load.
         * this is being patched with a `'false'` as a short-term solution, and instead,
         * these action options should be built conditionally if the source action has them
         */
        value: action.input.useJq ?? 'false',
      },
      jq_function: {
        ...actionOptionBaseData,
        id: transformedActionOptionIndexNumber + 5,
        key: 'jq_function',
        value: action.input.jqFunction ?? '',
      },
    };
  }

  if (action.type === 'output') {
    const attributeData = !action.output.publishedAttributeToken ? {
      name: {
        ...actionOptionBaseData,
        id: transformedActionOptionIndexNumber + 4,
        key: 'name',
        value: action.output.name,
      },
      data_type: {
        ...actionOptionBaseData,
        id: transformedActionOptionIndexNumber + 5,
        key: 'data_type',
        value: action.output.dataType,
      },
    } : {};

    actionOptionData = {
      use_jq: {
        ...actionOptionBaseData,
        id: transformedActionOptionIndexNumber + 1,
        key: 'use_jq',
        value: action.output.useJq || 'false',
        jsonValue: null,
      },
      jq_function: {
        ...actionOptionBaseData,
        id: transformedActionOptionIndexNumber + 2,
        key: 'jq_function',
        value: action.output.jqFunction ?? 'false',
        jsonValue: null,
      },
      expression: {
        ...actionOptionBaseData,
        id: transformedActionOptionIndexNumber + 3,
        key: 'expression',
        value: action.output.expression
          ? JSON.stringify(action.output.expression)
          : '',
        jsonValue: action.output.expression,
      },
      ...(
        action.options.category?.value && {
          category: {
            ...actionOptionBaseData,
            // this id is +6 because name and data_type above are +4 and +5
            id: transformedActionOptionIndexNumber + 6,
            key: 'category',
            value: action.options.category.value,
            jsonValue: null,
          },
        }
      ),
      ...(
        action.options?.output_value_range?.jsonValue
        && action.output.defineValueRanges !== false
        && {
          output_value_range: {
            ...actionOptionBaseData,
            id: transformedActionOptionIndexNumber + 7,
            key: 'output_value_range',
            value: JSON.stringify(action.options.output_value_range.value),
            jsonValue: action.options.output_value_range.value,
          },
        }
      ),
      ...attributeData,
    };
  }

  return { type: action.type, actionData, actionOptionData };
}

export function reverseTransformImportedDataPassingTypeAction({
  action,
  actionIndex,
  transformedActionOptionIndexNumber,
}: {
  action: Action;
  actionIndex: number;
  transformedActionOptionIndexNumber: number;
}) {
  let actionType;
  switch (action.type) {
    case ActionType.OUTCOME_REASON:
      actionType = 'outcomeReason';
      break;
    case ActionType.IMPORTED_MATRIX_MODEL:
      actionType = 'importedMatrixModel';
      break;
    case ActionType.IMPORTED_OUTPUT_ATTRIBUTE:
      actionType = 'importedOutputAttribute';
      break;
  }

  const actionData = {
    name: action[actionType]?.displayName,
    publishedAttributeToken: null,
    tagColor: action[actionType]?.color,
    customerTagId: null,
    applicationId: null,
    booleanRule: '',
    thresholdsRule: '',
    customerOutcomeId: null,
    options: {},
    precedence: null,
    serviceFailureDefault: null,
    serviceId: null,
  };

  const actionOptionBaseData = {
    publicId: action[actionType]?.actionOptionPublicId,
    publishedAttributeToken: null,
    actionId: actionIndex,
    id: 0,
    key: '',
    value: '',
    jsonValue: null,
  };

  const actionOptionData = {
    data_type: {
      ...actionOptionBaseData,
      id: transformedActionOptionIndexNumber + 1,
      key: 'data_type',
      value: action[actionType].dataType || '',
    },
    name: {
      ...actionOptionBaseData,
      id: transformedActionOptionIndexNumber + 2,
      key: 'name',
      value: action[actionType].name || '',
    },
    display_name: {
      ...actionOptionBaseData,
      id: transformedActionOptionIndexNumber + 3,
      key: 'display_name',
      value: action[actionType].displayName || '',
    },
    use_jq: {
      ...actionOptionBaseData,
      id: transformedActionOptionIndexNumber + 4,
      key: 'use_jq',
      value: action[actionType].useJq || 'false',
    },
  };

  return { type: action.type, actionData, actionOptionData };
}

export function reverseTransformMatrixModelTypeAction({
  action,
  actionIndex,
  transformedActionOptionIndexNumber,
}: {
  action: Action;
  actionIndex: number;
  transformedActionOptionIndexNumber: number;
}) {
  const matrixModelObject = action.matrixModel;
  const actionData = {
    name: matrixModelObject.name,
    publishedAttributeToken: null,
    tagColor: null,
    customerTagId: null,
    applicationId: null,
    booleanRule: '',
    thresholdsRule: '',
    customerOutcomeId: null,
    options: {},
    precedence: null,
    serviceFailureDefault: null,
    serviceId: null,
  };

  const actionOptionBaseData = {
    publicId: matrixModelObject.actionOptionPublicId,
    publishedAttributeToken: null,
    actionId: actionIndex,
    id: 0,
    key: '',
    value: '',
    jsonValue: null,
  };

  const is1DMatrix = isMatrix1Dimensional(matrixModelObject.axis.x);

  const matrixDataRouteX = {
    data_route_x: {
      ...actionOptionBaseData,
      id: transformedActionOptionIndexNumber + 1,
      key: 'data_route_x',
      value: matrixModelObject.dataRouteX,
    },
  };

  const matrixActionOptionIndex = !is1DMatrix
    ? matrixDataRouteX.data_route_x.id
    : transformedActionOptionIndexNumber;

  const shouldDefineValueRanges = getShouldDefineValueRanges(
    action.options.output_value_range,
    matrixModelObject.defineValueRanges,
  );

  const actionOptionData = {
    ...(!is1DMatrix ? matrixDataRouteX : {}),
    data_route_y: {
      ...actionOptionBaseData,
      id: matrixActionOptionIndex + 1,
      key: 'data_route_y',
      value: matrixModelObject.dataRouteY,
    },
    matrix_model_public_version: {
      ...actionOptionBaseData,
      id: matrixActionOptionIndex + 2,
      key: 'matrix_model_public_version',
      value: matrixModelObject.matrixModelPublicVersion,
    },
    matrix_model_token: {
      ...actionOptionBaseData,
      id: matrixActionOptionIndex + 3,
      key: 'matrix_model_token',
      value: matrixModelObject.matrixModelToken,
    },
    ...(action.options.description && {
      description: {
        ...actionOptionBaseData,
        id: matrixActionOptionIndex + 4,
        key: 'description',
        value: action.options.description.value || '',
      },
    }),
    ...(shouldDefineValueRanges && {
      output_value_range: {
        ...actionOptionBaseData,
        id: matrixActionOptionIndex + 5,
        key: 'output_value_range',
        jsonValue: action.options.output_value_range.jsonValue,
        value: JSON.stringify(action.options.output_value_range.value),
      },
    }),
  };

  const matrixData: LegacyMatrixModelResponse = {
    actionId: actionIndex,
    matrix: matrixModelObject.matrix,
    matrix_data_type: matrixModelObject.matrixDataType,
    name: matrixModelObject.name,
    x_axis_header: matrixModelObject.axis.x.axisHeader,
    x_axis_header_data_type: matrixModelObject.axis.x.axisHeaderDataType,
    x_order: matrixModelObject.axis.x.order || undefined,
    y_axis_header: matrixModelObject.axis.y.axisHeader,
    y_axis_header_data_type: matrixModelObject.axis.y.axisHeaderDataType,
    y_order: matrixModelObject.axis.y.order || undefined,
  };

  // Only include these fields if the matrix model already exists (not a temp token)
  // Temp tokens start with "TEMP-MM-" and indicate new matrix models that haven't been saved yet
  const isTempToken = matrixModelObject.matrixModelToken?.startsWith('TEMP-MM-');
  if (matrixModelObject.matrixModelToken && !isTempToken) {
    matrixData.active_matrix_model_version_id = matrixModelObject.activeMatrixModelVersionId;
    matrixData.matrix_model_id = matrixModelObject.matrixModelId;
    matrixData.matrix_model_token = matrixModelObject.matrixModelToken;
    matrixData.version = matrixModelObject.matrixModelPublicVersion;
  }

  return {
    actionData,
    actionOptionData,
    matrixData,
  };
}

export function reverseTransformWorkflowTypeAction({
  workflowAction,
}: {
  workflowAction: Action;
},
) {
  const { workflow: workflowActionMetadata } = workflowAction;
  const workflowActionData: Partial<LegacyAction> = {
    applicationId: workflowActionMetadata.workflowId,
    booleanRule: '',
    customerOutcomeId: null,
    customerTagId: null,
    name: workflowActionMetadata.name,
    precedence: workflowActionMetadata.precedenceIndex,
    publishedAttributeToken: null,
    serviceFailureDefault: null,
    serviceId: null,
    tagColor: null,
    thresholdsRule: '',
    options: {},
  };

  return workflowActionData;
}


export function reverseTransformExitTypeAction({
  exitAction,
}: {
  exitAction: Action;
}) {
  const { exit: exitActionMetadata } = exitAction;

  const exitActionData: Partial<LegacyAction> = {
    applicationId: null,
    booleanRule: '',
    customerOutcomeId: null,
    customerTagId: null,
    name: exitActionMetadata.name,
    precedence: null,
    publishedAttributeToken: null,
    serviceFailureDefault: null,
    serviceId: null,
    tagColor: null,
    thresholdsRule: '',
    options: {},
  };

  return exitActionData;
}
